import type { AccessConstraint } from '@sb/shared/access';
import { featureFlag } from '@sb/shared/feature';
import { PlanType } from '@shiftbase-com/ts-shared/models';

/** Access constraints for the routes rendered in the **`modal` auxiliary outlet** (see `modal.module.ts`). */
export const modalAccess = {
  // Scheduling
  availability: { subscriptionPlan: PlanType.BASIC },
  'calendar-sync': { subscriptionPlan: PlanType.BASIC },
  'calendar-sync/add': { subscriptionPlan: PlanType.BASIC },
  'calendar-sync/:id': { subscriptionPlan: PlanType.BASIC },
  'send-schedule': { permission: ['Create roster'], subscriptionPlan: PlanType.BASIC },
  'publish-schedule/:period/:date': { permission: ['Create roster'], subscriptionPlan: PlanType.PREMIUM },
  'manage-forecast/:date': { subscriptionPlan: PlanType.PREMIUM },
  'import-forecast': {
    permission: ['Edit forecast'],
    subscriptionPlan: PlanType.PREMIUM,
    featureFlag: featureFlag.TMP_FORECASTED_TURNOVER_IMPORT,
  },
  'manage-budget/:date': { subscriptionPlan: PlanType.PREMIUM },
  'exchange-request': { subscriptionPlan: PlanType.BASIC },
  'exchange-request/:id': { subscriptionPlan: PlanType.BASIC },
  'exchange-request/:id/edit': { subscriptionPlan: PlanType.BASIC },
  'schedule-open-shift': { subscriptionPlan: PlanType.BASIC },
  'schedule-open-shift/:id': { subscriptionPlan: PlanType.BASIC },
  'schedule-open-shift/:id/assign': { subscriptionPlan: PlanType.BASIC },
  'schedule-open-shift/:id/withdraw': { subscriptionPlan: PlanType.BASIC },
  'schedule-open-shift/:id/request': { subscriptionPlan: PlanType.BASIC },
  'schedule-open-shift/:id/edit': { subscriptionPlan: PlanType.BASIC },
  'schedule-open-shift/:id/delete': { subscriptionPlan: PlanType.BASIC },
  'schedule-required-shift': { permission: ['Create required shift'], subscriptionPlan: PlanType.BASIC },
  'schedule-required-shift/:id': { permission: ['View required shifts'], subscriptionPlan: PlanType.BASIC },
  'schedule-required-shift/:id/edit': { permission: ['Edit required shift'], subscriptionPlan: PlanType.BASIC },
  'schedule-required-shift/:id/delete': { permission: ['Delete required shift'], subscriptionPlan: PlanType.BASIC },

  // Timesheet / Clocking
  'open-close-days/:period/:date': { subscriptionPlan: PlanType.PREMIUM },
  timesheet: { permission: ['Create timesheets', 'Create own timesheet'] },
  'timesheet/:id': { permission: ['View all timesheets'] },
  'timesheet/:id/edit': {
    permission: [
      'Edit timesheets',
      'Edit own timesheet',
      'Approve timesheets',
      'Edit clocked time',
      'Edit own clocked time',
    ],
  },
  'clock-employee': { subscriptionPlan: PlanType.BASIC },
  clock: { subscriptionPlan: PlanType.BASIC },
  'clock-switch': { subscriptionPlan: PlanType.PREMIUM },

  // Employees
  'employee-file/:employeeId': { subscriptionPlan: PlanType.BASIC },
  'employee-file/:employeeId/:attachmentId': { subscriptionPlan: PlanType.BASIC },
  'employee-file-upload/:employeeId': {
    featureFlag: featureFlag.TMP_EMPLOYEE_FILES_TAB,
    permission: ['Add user uploads', 'Add own user uploads'],
    subscriptionPlan: PlanType.BASIC,
  },
  'employee-file-details/:employeeId': {
    featureFlag: featureFlag.TMP_EMPLOYEE_FILES_TAB,
    permission: ['Add user uploads', 'Add own user uploads'],
    subscriptionPlan: PlanType.BASIC,
  },
  // The flag is the add-on that owns succession, not the Files tab move its neighbours gate.
  'employee-file-succeed/:employeeId/:attachmentId': {
    featureFlag: featureFlag.TMP_SMART_DOCUMENT_MANAGEMENT,
    permission: ['Add user uploads', 'Add own user uploads'],
    subscriptionPlan: PlanType.BASIC,
  },
  'employee-file-preview/:employeeId/:attachmentId': {
    featureFlag: featureFlag.TMP_EMPLOYEE_FILES_TAB,
    permission: ['View user uploads', 'View own public uploads'],
    subscriptionPlan: PlanType.BASIC,
  },
  'employee-file-preview/:employeeId/:attachmentId/edit': {
    featureFlag: featureFlag.TMP_EMPLOYEE_FILES_TAB,
    permission: ['Edit user uploads', 'Edit own user uploads'],
    subscriptionPlan: PlanType.BASIC,
  },
  'employee-create': { permission: ['Create users'] },
  'minijob-payout': { permission: ['Edit minijob balances'], subscriptionPlan: PlanType.BASIC },
  contract: { subscriptionPlan: PlanType.BASIC },
  'contract/:contractId': { subscriptionPlan: PlanType.BASIC },
  extension: { permission: 'Create contracts', subscriptionPlan: PlanType.BASIC },
  'onboarding-invite-employees': { permission: ['Create users', 'Edit users', 'Edit user teams'] },

  // Absence / Balances
  // The 'own' variants matter: this modal is also how an employee requests their own absence.
  'add-absence': {
    permission: ['Create absentee', 'Create own absentee', 'Approve absentee'],
    subscriptionPlan: PlanType.BASIC,
  },
  'absence-request/:id': { permission: ['View absentee', 'View own absentee'] },
  // The editor itself, not the read-only request modal — viewing is not a reason to reach it.
  'absence-edit/:id': {
    permission: ['Edit absentee', 'Edit own absentee', 'Approve absentee', 'Approve own absentee'],
  },
  'absentee-option': { permission: 'Manage account', subscriptionPlan: PlanType.PREMIUM },
  'absentee-option/:id': { permission: 'Manage account', subscriptionPlan: PlanType.PREMIUM },
  'time-off-correction/:correctionType/:employeeId': {
    permission: ['Edit plus min hours', 'Adjust time off balances'],
    subscriptionPlan: PlanType.BASIC,
  },
  'time-off-correction/:correctionType/:employeeId/:balanceId': {
    permission: ['Edit plus min hours', 'Adjust time off balances'],
    subscriptionPlan: PlanType.BASIC,
  },
  'time-off-contract-type': { subscriptionPlan: PlanType.PREMIUM },
  'time-off-contract-type/:id': { subscriptionPlan: PlanType.BASIC },
  'absence-restriction': { subscriptionPlan: PlanType.PREMIUM },
  'absence-restriction/:id': { subscriptionPlan: PlanType.PREMIUM },
  'time-off-balances': { subscriptionPlan: PlanType.PREMIUM },
  'time-off-balances/:id': { subscriptionPlan: PlanType.BASIC },
  'add-absence-policy': { permission: ['Manage account'], subscriptionPlan: PlanType.PREMIUM },
  'activate-absence-type/:policyId/:absenceTypeId': {
    permission: ['Manage account'],
    subscriptionPlan: PlanType.BASIC,
  },

  // Settings
  'company-file': { subscriptionPlan: PlanType.BASIC },
  'company-file/:attachmentId': { subscriptionPlan: PlanType.BASIC },
  skill: { subscriptionPlan: PlanType.PREMIUM },
  'skill/:id': { subscriptionPlan: PlanType.PREMIUM },
  'skill-group': { subscriptionPlan: PlanType.PREMIUM },
  'skill-group/:id': { subscriptionPlan: PlanType.PREMIUM },
  'rate-card': { subscriptionPlan: PlanType.BASIC },
  'rate-card/copy/:id': { subscriptionPlan: PlanType.BASIC },
  'rate-card/:id': { subscriptionPlan: PlanType.BASIC },
  surcharge: { subscriptionPlan: PlanType.BASIC },
  holiday: { subscriptionPlan: PlanType.BASIC },
  'holiday/:id': { subscriptionPlan: PlanType.BASIC },
  'holiday-group': { permission: ['Manage account'], subscriptionPlan: PlanType.BASIC },
  'holiday-import': { subscriptionPlan: PlanType.BASIC },
  'clock-ip-address': { subscriptionPlan: PlanType.PREMIUM },
  'clock-ip-address/:id': { subscriptionPlan: PlanType.PREMIUM },
  kiosk: { subscriptionPlan: PlanType.BASIC },
  'kiosk/:id': { subscriptionPlan: PlanType.BASIC },
  'custom-fields/:appliesTo': { subscriptionPlan: PlanType.PREMIUM },
  'custom-fields/:appliesTo/:id': { subscriptionPlan: PlanType.PREMIUM },
  'overtime-policies': { subscriptionPlan: PlanType.PREMIUM },
  'overtime-policies/:id': { subscriptionPlan: PlanType.PREMIUM },
  'delete-team/:teamId': { permission: ['Manage account'] },
  'deactivate-department/:departmentId': { permission: ['Manage account'] },

  // Integrations
  integration: { subscriptionPlan: PlanType.PREMIUM },
  'integration/:id': { subscriptionPlan: PlanType.PREMIUM },
  'tonit-configure': { subscriptionPlan: PlanType.BASIC },
  'tonit-configure/:id': { subscriptionPlan: PlanType.BASIC },
  'app-token': { subscriptionPlan: PlanType.BASIC },
  'app-token/:id': { subscriptionPlan: PlanType.BASIC },
  'datafox-badge': { subscriptionPlan: PlanType.BASIC },
  'datafox-bulk-badges': { subscriptionPlan: PlanType.BASIC },
  'hr-salaris-gemak-configure': { subscriptionPlan: PlanType.BASIC },
  'hr-salaris-gemak-configure/:id': { subscriptionPlan: PlanType.BASIC },

  // Reports
  'report-favorite/save': { permission: ['View reports'], subscriptionPlan: PlanType.PREMIUM },
  'report-favorite/list': { permission: ['View reports'], subscriptionPlan: PlanType.PREMIUM },
  'report-recurring/save': { permission: ['View reports'], subscriptionPlan: PlanType.PREMIUM },
  'report-recurring/:id/edit': { permission: ['View reports'], subscriptionPlan: PlanType.PREMIUM },
  'report-recurring/list': { permission: ['View reports'], subscriptionPlan: PlanType.PREMIUM },

  // Billing
  'billing/details': { permission: ['Manage account'] },
  'billing/plan-wizard': { permission: ['Manage account'] },
  subscription: { permission: ['Manage account'] },
  'subscription-confirmation': { permission: ['Manage account'] },

  // Insights / Turnover
  'add-turnover': { permission: ['Edit turnover'] },
  'import-turnover': { permission: ['Edit turnover'], subscriptionPlan: PlanType.PREMIUM },

  // Diary
  diary: { permission: ['Edit diary'] },
  'diary-view': { permission: ['View diary'] },

  // Onboarding
  'onboarding-welcome': { featureFlag: featureFlag.TMP_ONBOARDING_WELCOME },
} satisfies Record<string, AccessConstraint>;
